import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, KeyRound, LogOut } from 'lucide-react';
import { Button, cn } from '@mlm/ui';
import { logout } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { NAV, isChildActive, isGroupActive, visibleNav } from './nav-items';

const EXPAND_KEY = 'mlm.cashier.sidebar.expanded';

/**
 * Cashier shell navigation. Labels when expanded, icons when collapsed.
 *
 * Deliberately a copy of the admin sidebar's behaviour and class strings
 * rather than a shared component: the admin one is welded to admin's auth
 * store, site manifest and route table, and generalising it would mean
 * rewriting production-critical admin surface inside a cashier change. Promote
 * to packages/ui when a third app needs it.
 */
export function CashierSidebar({
  collapsed,
  onToggleCollapsed,
  onNavigate,
  className,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  /** Called after any nav click — used by the mobile sheet to close itself. */
  onNavigate?: () => void;
  className?: string;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const items = visibleNav(NAV, {
    isSuper: !!user?.roles.includes('super_admin'),
    perms: user?.perms ?? [],
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      return JSON.parse(window.localStorage.getItem(EXPAND_KEY) ?? '{}');
    } catch {
      return {};
    }
  });

  // Auto-expand the active section so the cashier always sees where they are.
  useEffect(() => {
    const active = items.find((i) => i.children && isGroupActive(i, pathname));
    if (active && !expanded[active.label]) {
      setExpanded((prev) => ({ ...prev, [active.label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    window.localStorage.setItem(EXPAND_KEY, JSON.stringify(expanded));
  }, [expanded]);

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase();

  return (
    <aside
      className={cn(
        'no-print border-r bg-card flex flex-col transition-[width] duration-150',
        collapsed ? 'w-[64px]' : 'w-[240px]',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center h-14 border-b px-4 shrink-0',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {!collapsed ? (
          <span className="font-semibold text-sm tracking-tight">Cashier POS</span>
        ) : null}
        {onToggleCollapsed ? (
          <button
            type="button"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={onToggleCollapsed}
          >
            <ChevronLeft
              className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')}
            />
          </button>
        ) : null}
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {items.map((item) => {
            const groupActive = isGroupActive(item, pathname);
            const hasChildren = !!item.children?.length;
            const isOpen = !collapsed && hasChildren && (groupActive || expanded[item.label]);
            return (
              <li key={item.label}>
                <div className="flex items-stretch">
                  <NavLink
                    to={item.to}
                    onClick={onNavigate}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors flex-1',
                      collapsed && 'justify-center px-0',
                      groupActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!collapsed ? <span className="flex-1">{item.label}</span> : null}
                  </NavLink>
                  {hasChildren && !collapsed ? (
                    <button
                      type="button"
                      aria-label={`Toggle ${item.label}`}
                      onClick={() => setExpanded((p) => ({ ...p, [item.label]: !p[item.label] }))}
                      className="px-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <ChevronDown
                        className={cn('h-4 w-4 transition-transform', !isOpen && '-rotate-90')}
                      />
                    </button>
                  ) : null}
                </div>
                {hasChildren && isOpen ? (
                  <ul className="mt-0.5 ml-7 mb-1 border-l border-border pl-3 space-y-0.5">
                    {item.children!.map((child) => (
                      <li key={child.to}>
                        <NavLink
                          to={child.to}
                          onClick={onNavigate}
                          className={cn(
                            'block rounded-md px-2 py-1.5 text-xs transition-colors',
                            isChildActive(child, pathname)
                              ? 'text-primary font-medium'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Identity + account actions. Changing your own password needs no
          permission, so it lives here rather than in the perm-filtered NAV. */}
      <div className={cn('border-t p-2 space-y-1 shrink-0', collapsed && 'px-1')}>
        {collapsed ? (
          <div
            className="mx-auto mb-1 h-7 w-7 rounded-full bg-secondary text-[10px] font-medium flex items-center justify-center"
            title={user?.email ?? ''}
          >
            {initials}
          </div>
        ) : (
          <div className="px-2 pb-1">
            <div className="text-xs text-muted-foreground truncate" title={user?.email ?? ''}>
              {user?.email}
            </div>
            {user?.roles.length ? (
              <div className="mt-1 inline-block px-2 py-0.5 rounded bg-secondary text-[10px] font-medium">
                {user.roles.join(', ')}
              </div>
            ) : null}
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className={cn('w-full', collapsed ? 'px-0' : 'justify-start')}
          title={collapsed ? 'Change password' : undefined}
          onClick={() => {
            navigate('/change-password');
            onNavigate?.();
          }}
        >
          <KeyRound className="h-4 w-4 shrink-0" />
          {!collapsed ? <span className="ml-2">Change password</span> : null}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={cn('w-full', collapsed ? 'px-0' : 'justify-start')}
          title={collapsed ? 'Sign out' : undefined}
          onClick={onLogout}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed ? <span className="ml-2">Sign out</span> : null}
        </Button>
      </div>
    </aside>
  );
}
